import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { always, equals, ifElse, isNil, path, prop, when } from 'ramda';
import { isFalse, isNilOrEmpty } from 'ramda-adjunct';

const FEEDBACK_MS = 1500;

const STATUS_IDLE = 'idle';
const STATUS_COPIED = 'copied';
const STATUS_FAILED = 'failed';

const FEEDBACK_LABELS = {
  [STATUS_IDLE]: 'Copier',
  [STATUS_COPIED]: 'Copié !',
  [STATUS_FAILED]: 'Copie impossible'
};

const isUnmounted = (ref) => isFalse(prop('current', ref));

const VariableChip = ({ name, description }) => {
  const [status, setStatus] = useState(STATUS_IDLE);
  const timeoutRef = useRef();
  const isMountedRef = useRef(true);

  // The timeout and the clipboard promise both outlive the component when the panel
  // is collapsed or the widget modal closed right after a copy.
  useEffect(() => () => {
    isMountedRef.current = false;
    clearTimeout(prop('current', timeoutRef));
  }, []);

  const showFeedback = (nextStatus) => {
    if (isUnmounted(isMountedRef)) return;
    setStatus(nextStatus);
    clearTimeout(prop('current', timeoutRef));
    timeoutRef.current = setTimeout(() => {
      if (isUnmounted(isMountedRef)) return;
      setStatus(STATUS_IDLE);
    }, FEEDBACK_MS);
  };

  const onCopy = async () => {
    // `navigator.clipboard` is undefined outside secure contexts (and in jsdom), and
    // writeText rejects when the permission is denied or the document is not focused.
    const writeText = path(['clipboard', 'writeText'], navigator);
    if (isNil(writeText)) return showFeedback(STATUS_FAILED);

    try {
      await navigator.clipboard.writeText(`{{${name}}}`);
      showFeedback(STATUS_COPIED);
    } catch (error) {
      console.error('Clipboard write failed', error);
      showFeedback(STATUS_FAILED);
    }
  };

  const feedbackLabel = prop(status, FEEDBACK_LABELS);
  const tooltipTitle = when(isNilOrEmpty, always(name))(description);
  const announcement = ifElse(
    equals(STATUS_IDLE),
    always(''),
    always(`${feedbackLabel} ${name}`)
  )(status);

  // `relative` on the root is load-bearing: the status node below is `sr-only`, i.e.
  // `position: absolute`. Without a positioned ancestor inside the scrolling list, its
  // containing block would be the nearest positioned ancestor outside the scroller
  // (`.ant-modal-content`), so it would escape clipping and push the modal's
  // scrollHeight up to the unclipped height of the list (measured: +2500px).
  return (
    <div className="variable-chip relative flex items-center justify-between gap-2 w-full rounded px-2 py-1 bg-flexi-select-active">
      <Tooltip title={tooltipTitle}>
        <span className="truncate text-xs text-flexi-text">{name}</span>
      </Tooltip>
      <Tooltip title={feedbackLabel}>
        <button
          type="button"
          aria-label={`Copier la variable ${name}`}
          className="variable-chip-copy shrink-0 cursor-pointer border-none bg-transparent p-0 text-flexi-text"
          onClick={onCopy}
        >
          <CopyOutlined />
        </button>
      </Tooltip>
      {/* Hover tooltips are invisible to screen readers and keyboard users. */}
      <span className="sr-only" role="status" aria-live="polite">
        {announcement}
      </span>
    </div>
  );
};

export default VariableChip;
